/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react'
import { Meta, Title, Link as HeadLink } from 'react-head'
import { Link } from 'react-router-dom'
import DefaultLayout from 'js/components/DefaultLayout'
import { Navigation } from 'js/pages/Home/Navigation'
import PartnershipLogo from './components/PartnershipLogo'
import Footer from 'js/components/Footer/Footer'
import { runQuery } from 'js/dato'
import withSSR from 'js/components/withSSR/withSSR'
import ErrorPage from '../ErrorPage'
import Spinner from 'js/components/Spinner/Spinner'
import { BLOG_POST_QUERY } from './queries/blogPostQuery'
import { BLOG_UI_TEXT_QUERY } from './queries/blogUITextQuery'
import type { BlogPostResponse } from './types'
import Markdown from 'markdown-to-jsx'
import DatePill from './components/DatePill'
import Pill from './components/Pill'
import PostInList from './components/PostInList'
import useLocale from 'js/hooks/useLocale'
import './blog.css'

type Props = {
  data: {
    blogPost: BlogPostResponse['blogPost'] | null
    morePosts: BlogPostResponse['morePosts']
    uiTexts: Array<{ slug: string; text: string }>
  } | null
  isLoading: boolean
  error: Error | null
}

function BlogPostPage(props: Props) {
  const { data, isLoading, error } = props
  const { blogPost, morePosts, uiTexts } = data || {}
  const { language } = useLocale()

  if (isLoading) {
    return (
      <DefaultLayout>
        <div style={styles.loading}>
          <Spinner width="50" color="green" />
        </div>
      </DefaultLayout>
    )
  }

  if (error) {
    return <ErrorPage error={error} />
  }
  if (!blogPost) {
    return <ErrorPage error={new Error('Blog post not found')} />
  }

  const canonicalUrl = `https://lifesum.com/${language}/blog/${blogPost.slug}/`

  // Get UI text for back link
  const backToAllPostsText =
    uiTexts?.find((text) => text.slug === 'back-to-all-posts')?.text ||
    'Back to all posts'
  const byText = uiTexts?.find((text) => text.slug === 'by')?.text || 'by'
  const relatedArticlesText =
    uiTexts?.find((text) => text.slug === 'related-articles')?.text ||
    'More Posts'

  return (
    <DefaultLayout>
      <Title>{blogPost.title} - Lifesum</Title>
      <Meta name="description" content={blogPost.seo?.description || ''} />
      <HeadLink rel="canonical" href={canonicalUrl} />
      <div className="blog-nav-spacer">
        <Navigation
          customLogo={<PartnershipLogo theme="light" />}
          theme="light"
          minimal={false}
        />
      </div>
      <article style={styles.container}>
        <Link to={`/${language}/blog`} style={styles.backLink}>
          ← {backToAllPostsText}
        </Link>
        <header style={styles.header}>
          <h1 style={styles.title}>{blogPost.title}</h1>
          <div style={styles.meta}>
            {blogPost.seo?.description && (
              <p style={styles.description}>{blogPost.seo.description}</p>
            )}
            <div style={styles.pills}>
              <DatePill date={blogPost._firstPublishedAt} />
              {blogPost.authorName && (
                <Pill variant="light">
                  {byText} {blogPost.authorName}
                </Pill>
              )}
            </div>
          </div>
        </header>
        {blogPost.featuredImage && (
          <div style={styles.featuredImage}>
            <img
              src={blogPost.featuredImage.responsiveImage.src}
              srcSet={blogPost.featuredImage.responsiveImage.srcSet}
              sizes={blogPost.featuredImage.responsiveImage.sizes}
              alt={blogPost.featuredImage.responsiveImage.alt || blogPost.title}
              style={styles.featuredImageImg}
            />
          </div>
        )}
        <div style={styles.content} className="blog-content">
          <Markdown>{blogPost.article}</Markdown>
        </div>
        {morePosts && morePosts.length > 0 && (
          <div style={styles.relatedPosts}>
            <h2 style={styles.relatedTitle}>{relatedArticlesText}</h2>
            <div style={styles.relatedGrid}>
              {morePosts.map((post) => (
                <PostInList key={post.id} post={post} uiTexts={uiTexts} />
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </DefaultLayout>
  )
}

BlogPostPage.getInitialProps = async ({ params, language }) => {
  const slug = params?.slug
  if (!slug) return { blogPost: null }

  const locale = language || 'en'

  // Try to fetch in requested language
  let [blogData, uiTextData] = await Promise.all([
    runQuery(BLOG_POST_QUERY, { slug, locale }),
    runQuery(BLOG_UI_TEXT_QUERY, { locale }),
  ])

  if (!blogData.blogPost || !blogData.blogPost.title) {
    // Fallback to English
    ;[blogData, uiTextData] = await Promise.all([
      runQuery(BLOG_POST_QUERY, { slug, locale: 'en' }),
      runQuery(BLOG_UI_TEXT_QUERY, { locale: 'en' }),
    ])
  }

  return {
    ...blogData,
    uiTexts: uiTextData?.allBlogUiTexts || [],
  }
}

export default withSSR(BlogPostPage)

const styles = {
  container: {
    maxWidth: 'var(--content-width-lg)',
    margin: 'var(--spacing-xl) auto',
    padding: 'var(--spacing-xl) var(--spacing-md)',
  },
  backLink: {
    display: 'inline-block',
    color: 'var(--color-text)',
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm)',
  },
  header: {
    borderTop: `1px solid var(--color-text)`,
    paddingTop: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-xl)',
  },
  title: {
    fontSize: 'var(--font-size-5xl)',
    fontWeight: 700,
    color: 'var(--color-text)',
    lineHeight: 'var(--line-height-heading)',
    marginBottom: 'var(--spacing-xs)',
  },
  meta: {
    marginBottom: '0',
  },
  description: {
    fontSize: 'var(--font-size-2xl)',
    lineHeight: 'var(--line-height-content)',
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-md)',
  },
  pills: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
    alignItems: 'center',
  },
  featuredImage: {
    marginBottom: 'var(--spacing-2xl)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-md)',
  },
  featuredImageImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  content: {
    fontSize: 'var(--font-size-lg)',
    lineHeight: 'var(--line-height-content)',
    color: 'var(--color-text)',
    maxWidth: 'var(--content-width-sm)',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  relatedPosts: {
    marginTop: 'var(--spacing-3xl)',
    paddingTop: 'var(--spacing-2xl)',
  },
  relatedTitle: {
    fontSize: 'var(--font-size-3xl)',
    fontWeight: 700,
    marginBottom: 'var(--spacing-xl)',
    textAlign: 'center' as const,
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 'var(--spacing-xl)',
    maxWidth: 'var(--content-width-md)',
    margin: '0 auto',
  },
  byText: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--color-text-light)',
  },
}
